
import SocialFeedItemsView from '@/components/social/SocialFeedItemsView.vue';
import SocialFeedPagination from '@/components/social/SocialFeedPagination.vue';
import SocialFeedPostDetailModal from '@/components/social/SocialFeedPostDetailModal.vue';
import { GENERIC_ERROR_DESCRIPTION } from '@/constants';
import { BotMixin } from '@/mixins/BotMixin';
import { SocialFeedItem } from '@/types/socialFeed';
import {
  clampSocialFeedPage,
  fetchAllSocialFeedItems,
  getConnectedSocialFeedPlatforms,
  paginateSocialFeedItems,
  PROJECT_SOCIAL_FEED_PAGE_SIZE,
} from '@/utils/socialFeed';
import { mixins } from 'vue-class-component';
import { Component, Watch } from 'vue-property-decorator';

@Component({
  name: 'social-posting-latest-posts-card',
  components: { SocialFeedItemsView, SocialFeedPagination, SocialFeedPostDetailModal },
})
export default class SocialPostingLatestPostsCard extends mixins(BotMixin) {
  readonly pageSize = PROJECT_SOCIAL_FEED_PAGE_SIZE;

  items: SocialFeedItem[] = [];
  currentPage = 1;
  isLoading = false;
  isRefreshing = false;
  loadError: string | null = null;
  partialLoadError = false;
  showDetailModal = false;
  selectedItem: SocialFeedItem | null = null;

  get hasConnectedPlatforms(): boolean {
    return getConnectedSocialFeedPlatforms(this.bot).length > 0;
  }

  get paginatedItems(): SocialFeedItem[] {
    return paginateSocialFeedItems(this.items, this.currentPage, this.pageSize);
  }

  @Watch('bot', { immediate: true, deep: true })
  async onBotChange() {
    if (!this.hasConnectedPlatforms) {
      this.items = [];
      this.currentPage = 1;
      this.loadError = null;
      this.partialLoadError = false;
      return;
    }
    await this.loadPosts();
  }

  onOpenPost(item: SocialFeedItem) {
    this.selectedItem = item;
    this.showDetailModal = true;
  }

  onPostDeleted(item: SocialFeedItem) {
    this.items = this.items.filter(
      (existing) => !(existing.id === item.id && existing.platform === item.platform),
    );
    this.selectedItem = null;
    this.syncCurrentPage();
  }

  goToPreviousPage() {
    this.currentPage = clampSocialFeedPage(this.currentPage - 1, this.items.length, this.pageSize);
  }

  goToNextPage() {
    this.currentPage = clampSocialFeedPage(this.currentPage + 1, this.items.length, this.pageSize);
  }

  syncCurrentPage() {
    this.currentPage = clampSocialFeedPage(this.currentPage, this.items.length, this.pageSize);
  }

  async loadPosts(force = false) {
    if (!this.hasConnectedPlatforms) {
      return;
    }
    if (!force && this.items.length) {
      return;
    }
    if (force) {
      this.isRefreshing = true;
    } else {
      this.isLoading = true;
    }
    this.loadError = null;
    this.partialLoadError = false;
    try {
      const { items, results } = await fetchAllSocialFeedItems(this.bot);
      this.items = items;
      this.currentPage = 1;
      this.partialLoadError = results.some((result) => result.error);
      if (!items.length && results.every((result) => result.error)) {
        this.loadError = GENERIC_ERROR_DESCRIPTION;
      }
    } catch (e) {
      this.loadError = GENERIC_ERROR_DESCRIPTION;
      this.items = [];
    } finally {
      if (force) {
        this.isRefreshing = false;
      } else {
        this.isLoading = false;
      }
    }
  }
}
